/**
 * VideoJs VMAP plugin
 * This plugin load VMAP xml and parse it with internal VMAP parser and show linear ads.
 * This plugin can show gif, png, jpeg and mp4 ads.
 */
/// <reference path="../../types/videojs.d.ts"/>
import { VMAPParser } from "../parser/index";
import { AdBreakType } from "../definitions/VMAP";
import { mimetype } from "../definitions/VAST3";
import ImagePlayer from "../lib/image-player/index";
import VideoPlayer from "./videoPlayer";
import Controller from "../lib/controller/index";
import FP from "fingerprintjs2";
var VideoJsPlugin = /** @class */ (function () {
    /**
     * @constructor
     * @param {VideoJsStatic} videoJsPlayer
     * @param {Object} config
     * @param player
     * @param {HTMLElement} div
     */
    function VideoJsPlugin(videoJsPlayer, config, div) {
        /**
         * Show ad status flag
         */
        this.adIsShowing = false;
        /**  /**
         * Show ad status flag
         */
        this.timerLock = 0;
        this.mainVideoEnded = false;
        if (config.debug)
            console.debug("Init VideoJs Vast Plugin Class.");
        this.player = videoJsPlayer;
        this.config = config;
        this.div = div;
    }
    /**
     * @func setup
     * @desc setup plugin by load VMAP object and set player events
     */
    VideoJsPlugin.prototype.setup = function () {
        var _this = this;
        if (this.config.debug)
            console.debug("Setting VideoJs Vast Plugin up.");
        this.loadVMAP().then(function (vmap) {
            _this.VMAP = vmap;
            if (_this.config.debug)
                console.debug("VMAP object is loaded", vmap);
            _this.player.on("timeupdate", function () {
                _this.timeController();
            });
            _this.player.on('play', function () {
                if (_this.mainVideoEnded && _this.currentAdBreak && _this.currentAdBreak.timeOffset === "end") {
                    _this.mainVideoEnded = false;
                    _this.adIsShowing = false;
                    _this.showStartLinearAd();
                }
            });
        });
    };
    /**
     * @func timeController
     * @des get position of player timer and call necessary functions
     */
    VideoJsPlugin.prototype.timeController = function () {
        var position = {
            position: this.player.currentTime(),
            duration: this.player.duration(),
            type: "time"
        };
        if (this.timerLock === position.position - position.duration) {
            return;
        }
        this.timerLock = position.position - position.duration;
        if (position.position - position.duration === 0) {
            this.onVideoEnd(false);
        }
        else {
            if (this.overlayController)
                this.overlayController.setTimeLine(position);
        }
        if (!this.callCompleteViewApiLock && this.adIsShowing && position.position >= 30) {
            this.callCompleteViewApi();
        }
    };
    /**
     * @func onVideoEnd
     * @desc This function had to call when a video completed or skipped (in ads).
     *       At first the ad's controller must remove.
     *       This function control the state of player and insure that ad is playing or not.
     *       If the ad's state is playing, the ad player (image or video) had to stop and the main video had to play
     * @param skipped
     */
    VideoJsPlugin.prototype.onVideoEnd = function (skipped) {
        var _this = this;
        if (this.overlayController)
            this.overlayController.remove();
        if (this.adIsShowing) {
            if (!skipped) {
                this.callCompleteViewApi();
            }
            setTimeout(function () {
                _this.adIsShowing = false;
            }, 2000);
            if (this.currentAdBreak.timeOffset === "start") {
                if (!!this.videoPlayer) {
                    this.videoPlayer.skip(true);
                    this.videoPlayer = null;
                }
                else if (!!this.imagePlayer) {
                    this.imagePlayer.stop();
                    this.imagePlayer = null;
                    setTimeout(function () {
                        _this.player.play();
                    }, 100);
                }
            }
            else if (this.currentAdBreak.timeOffset === "end") {
                if (!!this.videoPlayer) {
                    this.videoPlayer.skip(false);
                    this.videoPlayer = null;
                }
                else if (!!this.imagePlayer) {
                    this.imagePlayer.stop();
                    this.imagePlayer = null;
                }
            }
        }
        else if (skipped === false) {
            setTimeout(function () {
                _this.showEndLinerAd();
            }, 100);
        }
    };
    /**
     * @func callCompleteViewApi
     * @desc call complete ad view's api if the type of ad is video.
     *       the api call will be lock for 2 seconds
     */
    VideoJsPlugin.prototype.callCompleteViewApi = function () {
        if (this.config.debug)
            console.debug("Call complete view api");
        if (this.callCompleteViewApiLock) {
            return;
        }
        this.callCompleteViewApiLock = true;
        var ad = this.currentAdBreak.adSource.VASTAdData.ads[0];
        var creative = ad.creative[0];
        var completeTrackingIndex = creative.trackings.findIndex(function (i) { return i.event === "complete"; });
        if (completeTrackingIndex === -1 ||
            this.currentAdBreak.adSource.VASTAdData.ads[0].creative[0].mediaFiles[0]
                .mimetype !== "video/mp4") {
            return;
        }
        if (this.config.debug)
            console.debug("Call complete view api with url: ", creative.trackings[completeTrackingIndex].uri);
        var xhr = new XMLHttpRequest();
        xhr.open("get", creative.trackings[completeTrackingIndex].uri);
        xhr.send();
    };
    /**
     * @func callImpressionAdi
     * @desc call impression's api.
     *       the api call will be lock for 2 seconds
     */
    VideoJsPlugin.prototype.callImpressionAdi = function () {
        var _this = this;
        if (this.config.debug)
            console.debug("Call impression api");
        if (this.callImpressionViewApiLock) {
            return;
        }
        this.callImpressionViewApiLock = true;
        setTimeout(function () {
            _this.callImpressionViewApiLock = false;
        }, 2000);
        var ad = this.currentAdBreak.adSource.VASTAdData.ads[0];
        var impression = ad.impressions[0];
        if (!impression)
            return;
        if (this.config.debug)
            console.debug("Call impression api with url: ", impression.uri);
        var imgDom = document.createElement("img");
        imgDom.src = impression.uri;
        imgDom.style.display = "none";
        this.div.appendChild(imgDom);
    };
    /**
     * @func showStartLinearAd
     * @desc show linear ad. this method check ad is not playing and check vmap object for start type of ad
     */
    VideoJsPlugin.prototype.showStartLinearAd = function () {
        var _this = this;
        if (this.config.debug)
            console.debug("show start liner ad");
        var skipShowAd = false;
        if (this.adIsShowing)
            return;
        this.adIsShowing = true;
        if (this.player.currentTime() >= 0.1)
            return;
        this.VMAP.breaks.forEach(function (adBreak) {
            if (adBreak.breakTypes[0] === AdBreakType.linear &&
                adBreak.timeOffset === "start" &&
                !skipShowAd) {
                _this.showAd(adBreak);
                skipShowAd = true;
            }
        });
    };
    /**
     * @func showEndLinerAd
     * @desc show linear ad. this method check ad is not playing and check vmap object for end type of ad
     */
    VideoJsPlugin.prototype.showEndLinerAd = function () {
        var _this = this;
        if (this.config.debug)
            console.debug("show end liner ad");
        var skipShowAd = false;
        setTimeout(function () {
            _this.mainVideoEnded = true;
        }, 1000);
        if (this.adIsShowing)
            return;
        this.adIsShowing = true;
        this.VMAP.breaks.forEach(function (adBreak) {
            if (adBreak.breakTypes[0] === AdBreakType.linear &&
                adBreak.timeOffset === "end" &&
                !skipShowAd) {
                _this.showAd(adBreak);
                skipShowAd = true;
            }
        });
    };
    /**
     * @func loadVMAP
     * @desc Load VMAP xml data and parse that
     * @param {string} url
     * @returns {Promise<IVMAP>}
     */
    VideoJsPlugin.prototype.loadVMAP = function (url) {
        if (url === void 0) { url = this.config.requestUrl; }
        var that = this;
        if (this.config.debug)
            console.debug("Try to load VideoJs VMAP from " + url);
        return new Promise(function (resolve, reject) {
            new FP().get(function (r) {
                var xhr = new XMLHttpRequest();
                xhr.onreadystatechange = function () {
                    if (this.readyState === 4 && this.status === 200) {
                        var parser = new VMAPParser(xhr.responseText, that.config.debug);
                        var VMAP = parser.JSON(xhr.responseText);
                        if (that.config.debug)
                            console.debug("VMAP loaded:", VMAP);
                        resolve(VMAP);
                    }
                };
                xhr.onerror = function () {
                    reject();
                    throw new Error("Failed to load VideoJs VMAP from " + url);
                };
                xhr.open("GET", "" + url + (url.indexOf('?') === -1 ? '?' : '&') + "tid=" + r + "&p=" + window.location.href + "&r=" + document.referrer);
                xhr.send();
            });
        });
    };
    /**
     * @func showAd
     * @desc Check ad's media type and show player
     * @param {IAdBreak} adBreak
     */
    VideoJsPlugin.prototype.showAd = function (adBreak) {
        if (this.config.debug)
            console.debug("show ad break", adBreak);
        this.currentAdBreak = adBreak;
        switch (adBreak.breakTypes[0]) {
            case AdBreakType.linear:
                var mediaFile = adBreak.adSource.VASTAdData.ads[0].creative[0].mediaFiles[0];
                switch (mediaFile.mimetype) {
                    case mimetype.IMAGE_GIF:
                    case mimetype.IMAGE_JPEG:
                    case mimetype.IMAGE_JPG:
                    case mimetype.IMAGE_PNG:
                        this.showImagePlayer(adBreak.adSource.VASTAdData.ads[0], adBreak);
                        break;
                    case mimetype.VIDEO_MP4:
                        this.showVideoPlayer(adBreak.adSource.VASTAdData.ads[0], adBreak);
                        break;
                    default:
                        throw new Error("Media file type \"" + mediaFile.mimetype + "\" is not support.");
                }
                break;
            default:
                throw new Error("AdBreak type \"" + this.VMAP.breaks[0].breakTypes[0] + "\" is not support.");
        }
    };
    /**
     * @func showImagePlayer
     * @desc Create imagePlayer object and set it's event.
     * @param {Ad} ad
     * @param {IAdBreak} adBreak
     */
    VideoJsPlugin.prototype.showImagePlayer = function (ad, adBreak) {
        var _this = this;
        if (this.config.debug)
            console.debug("show image player", ad, adBreak);
        var clickThroughUri = ad.creative[0].videoClicks.clickThrough.uri;
        var domainEx = adBreak.extensions
            ? adBreak.extensions.findIndex(function (e) { return e.extensionType === "domain"; })
            : -1;
        var skipAfter = adBreak.extensions
            ? adBreak.extensions.findIndex(function (e) { return e.extensionType === "skip"; })
            : -1;
        this.imagePlayer = new ImagePlayer(this.div, ad.creative[0].mediaFiles[0].uri, {});
        this.overlayController = new Controller(this.player, this.div, domainEx > -1 && adBreak.extensions
            ? adBreak.extensions[domainEx].value.toString()
            : "", clickThroughUri, skipAfter > -1 && adBreak.extensions
            ? parseInt(adBreak.extensions[skipAfter].value.toString(), 10)
            : 1000, true, this.config.debug);
        this.overlayController.setShowControllerBtn(false);
        this.imagePlayer.play();
        this.player.pause();
        var duration = parseInt(ad.creative[0].duration.replace(new RegExp(":", "g"), ""), 10);
        var timer = 0;
        var interval = setInterval(function () {
            timer++;
            if (timer - duration > 0) {
                if (_this.imagePlayer)
                    _this.imagePlayer.stop();
                _this.onVideoEnd(false);
                if (timer)
                    clearInterval(interval);
                return;
            }
            _this.overlayController.setTimeLine({
                duration: duration,
                position: timer,
                type: "time"
            });
        }, 1000);
        this.overlayController.setOnSkip(function () {
            if (_this.imagePlayer)
                _this.imagePlayer.stop();
            _this.onVideoEnd(true);
            _this.overlayController.remove();
            if (timer)
                clearInterval(interval);
        });
        this.overlayController.show();
        this.callImpressionAdi();
    };
    /**
     * @func showVideoPlayer
     * @desc Create videoPlayer object and set it's event.
     * @param {Ad} ad
     * @param {IAdBreak} adBreak
     */
    VideoJsPlugin.prototype.showVideoPlayer = function (ad, adBreak) {
        var _this = this;
        if (this.config.debug)
            console.debug("show video ad", ad);
        var clickThroughUri = ad.creative[0].videoClicks.clickThrough.uri;
        var domainEx = adBreak.extensions
            ? adBreak.extensions.findIndex(function (e) { return e.extensionType === "domain"; })
            : -1;
        var skipAfter = adBreak.extensions
            ? adBreak.extensions.findIndex(function (e) { return e.extensionType === "skip"; })
            : -1;
        this.videoPlayer = new VideoPlayer(this.player, this.div, ad.creative[0].mediaFiles[0].uri);
        this.overlayController = new Controller(this.player, this.div, (domainEx > -1 && adBreak.extensions)
            ? adBreak.extensions[domainEx].value.toString()
            : "", clickThroughUri, (skipAfter > -1 && adBreak.extensions)
            ? parseInt(adBreak.extensions[skipAfter].value.toString(), 10)
            : 1000, false, this.config.debug);
        this.overlayController.setShowControllerBtn(true);
        this.overlayController.setOnSkip(function () {
            _this.onVideoEnd(true);
            _this.overlayController.remove();
        });
        this.videoPlayer.play();
        this.overlayController.show();
        this.callImpressionAdi();
    };
    return VideoJsPlugin;
}());
export default VideoJsPlugin;
//# sourceMappingURL=plugin.js.map